import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DarkTiles } from "@/components/map/DarkTiles";
import { NearbyArtisans } from "@/components/map/NearbyArtisans";
import { MapRestaurants } from "@/components/map/MapRestaurants";
import { AccountMenuLinks } from "@/components/AccountMenu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WOMA_SERVICES, ServiceImg, useTapBounce } from "@/components/ServiceImages";
import { useLastPosition } from "@/components/GeoProvider";
import axios from "axios";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Truck,
  Wallet,
  QrCode,
  MapPin,
  ChevronRight,
  ArrowRight,
  Menu,
  Car,
  Search,
  Clock,
  LogOut, // eslint-disable-line no-unused-vars
  Coins,
} from "lucide-react";
import BottomSheet from "@/components/BottomSheet";
import DestinationPicker from "@/components/DestinationPicker";
import WelcomeBonusOnboarding, { useWelcomeBonusOnboarding } from "@/components/WelcomeBonusOnboarding";
import RoleSwitcher from "@/components/RoleSwitcher";
import ExploreDakarPanel from "@/components/map/ExploreDakarPanel";
import FavoritePlaces from "@/components/map/FavoritePlaces";
import { Compass } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recentre seulement si la position bouge vraiment (~100 m) pour ne pas gêner les clics sur la carte
const Recenter = ({ pos }) => {
  const map = useMap();
  const lat = pos[0].toFixed(3);
  const lng = pos[1].toFixed(3);
  useEffect(() => { map.setView([+lat, +lng], map.getZoom(), { animate: false }); }, [map, lat, lng]);
  return null;
};

const cleanHood = (name) =>
  (name || "")
    .replace(/^commune\s+d['’]\s*arrondissement\s+(des|de|du|d['’])\s*/i, "")
    .replace(/^(commune|arrondissement)\s+(des|de|du|d['’])\s*/i, "")
    .trim() || null;

// Fix Leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});


const SMALL_SERVICES = WOMA_SERVICES;

const EXTRA_SERVICES = [
  { key: "loyalty", title: "Coins", Icon: Coins, route: "/loyalty" },
  { key: "credits", title: "Crédits", Icon: Wallet, route: "/credits" },
  { key: "qr", title: "QR Codes", Icon: QrCode, route: "/qrcodes" },
];

const ServiceCard = ({ service, onClick, bouncing }) => {
  const Icon = service.Icon;
  const CustomIcon = service.CustomIcon;
  return (
    <button
      onClick={onClick}
      className="bg-[#1A1A1A] hover:bg-[#242424] rounded-2xl p-5 flex flex-col items-center justify-center h-[110px] transition-all active:scale-95"
      data-testid={`service-card-${service.key}`}
    >
      {service.img ? (
        <ServiceImg service={service} bouncing={bouncing} testId={`service-img-${service.key}`} />
      ) : CustomIcon ? (
        <CustomIcon />
      ) : (
        <Icon className="w-7 h-7 text-white" strokeWidth={1.8} />
      )}
      <span className="text-white mt-2 font-medium text-sm">{service.title}</span>
    </button>
  );
};

const RecentItem = ({ title, subtitle, time, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center w-full py-3.5 border-b border-[#2D2D2D] last:border-b-0 text-left hover:bg-[#1A1A1A] transition-colors px-1"
    data-testid="recent-address-row"
  >
    <div className="w-10 h-10 rounded-full bg-[#2D2D2D] flex items-center justify-center flex-shrink-0">
      <MapPin className="w-5 h-5 text-gray-300" />
    </div>
    <div className="flex-1 ml-4 min-w-0">
      <p className="text-white text-[15px] font-medium truncate">{title}</p>
      <p className="text-[#A0A0A0] text-xs mt-0.5 truncate">{subtitle}</p>
    </div>
    {time && <span className="text-[#A0A0A0] text-sm ml-2">{time}</span>}
  </button>
);

function formatRelativeTime(isoDate) {
  try {
    const then = new Date(isoDate);
    const diffMs = Date.now() - then.getTime();
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} h`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} j`;
    return then.toLocaleDateString("fr-FR");
  } catch {
    return null;
  }
}

function splitAddress(full) {
  if (!full) return { title: "", subtitle: "" };
  const parts = full.split(",").map((s) => s.trim()).filter(Boolean);
  return { title: parts[0] || full, subtitle: parts.slice(1, 3).join(", ") };
}

// Style sombre pour la carte via filter CSS (pas besoin de tileserver custom)
// eslint-disable-next-line no-unused-vars
export default function HomePage({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const { bouncing, tap } = useTapBounce();
  const [recentAddresses, setRecentAddresses] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showExplore, setShowExplore] = useState(false);
  const [hood, setHood] = useState(null);
  const livePos = useLastPosition();
  const hasLive = !!livePos;
  const [userPos, setUserPos] = useState([14.6928, -17.4467]); // Dakar default
  const { show: showWelcome, dismiss: dismissWelcome } = useWelcomeBonusOnboarding(user);

  const fetchRecentAddresses = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [res, saved] = await Promise.all([
        axios.get(`${API}/orders?role=client&customer_id=${user.id}&limit=20`),
        axios.get(`${API}/users/me/saved-places`).catch(() => ({ data: { places: [] } })),
      ]);
      const seen = new Set();
      const uniq = [];
      for (const p of saved.data.places || []) {
        const addr = [p.name, p.address].filter(Boolean).join(", ");
        if (seen.has(addr)) continue;
        seen.add(addr);
        uniq.push({ address: addr, date: p.last_used_at, lat: p.lat, lng: p.lng });
        if (uniq.length >= 3) break;
      }
      for (const o of res.data || []) {
        const addr = o.delivery_address;
        if (!addr || seen.has(addr)) continue;
        seen.add(addr);
        uniq.push({ address: addr, date: o.created_at });
        if (uniq.length >= 6) break;
      }
      setRecentAddresses(uniq);
    } catch (err) {
      console.warn("Failed to fetch recent addresses:", err);
      setRecentAddresses([]);
    }
  }, [user?.id]);

  const fetchUserLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos([pos.coords.latitude, pos.coords.longitude]),
      () => {},
      { timeout: 5000 }
    );
  }, []);

  // Position partagée par GeoProvider (mise à jour en continu)
  useEffect(() => {
    if (livePos) setUserPos([livePos.lat, livePos.lng]);
  }, [livePos]);

  // Nom du quartier (Grand Yoff, Parcelles Assainies…) affiché à côté de « Votre position »
  const hoodLat = userPos[0].toFixed(3);
  const hoodLng = userPos[1].toFixed(3);
  useEffect(() => {
    if (!hasLive && hoodLat === "14.693") return;
    let stale = false;
    fetch(`https://nominatim.openstreetmap.org/reverse?format=json&zoom=16&accept-language=fr&lat=${hoodLat}&lon=${hoodLng}`)
      .then((r) => r.json())
      .then((d) => {
        const a = d.address || {};
        if (!stale) setHood(cleanHood(a.suburb || a.neighbourhood || a.quarter || a.city_district || a.village || a.town || a.city));
      })
      .catch(() => {});
    return () => { stale = true; };
  }, [hoodLat, hoodLng, hasLive]);

  useEffect(() => {
    if (!user?.id) return;
    fetchRecentAddresses();
    fetchUserLocation();
  }, [user?.id, fetchRecentAddresses, fetchUserLocation]);

  const handleRecentClick = (addr) => {
    const params = new URLSearchParams({ delivery_address: addr.address });
    if (addr.lat != null) { params.set("delivery_lat", addr.lat); params.set("delivery_lng", addr.lng); }
    navigate(`/orders/new?${params.toString()}`);
  };

  const openDestinationPicker = () => {
    setShowPicker(true);
  };

  const handlePickerConfirm = (lat, lng, address) => {
    const params = new URLSearchParams({
      delivery_lat: lat,
      delivery_lng: lng,
      delivery_address: address || "",
    });
    setShowPicker(false);
    navigate(`/orders/new?${params.toString()}`);
  };

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      {/* CARTE EN ARRIÈRE-PLAN */}
      <div className="absolute inset-0 z-0 isolate" data-testid="home-map-bg">
        <MapContainer
          center={userPos}
          zoom={12}
          style={{ height: "100%", width: "100%", background: "#101010" }}
          zoomControl={false}
          attributionControl={false}
        >
          <DarkTiles />
          <Recenter pos={userPos} />
          <Marker position={userPos} />
          <NearbyArtisans pos={userPos} />
          <MapRestaurants />
        </MapContainer>
      </div>

      {/* HEADER overlay */}
      <header className="absolute top-0 inset-x-0 z-30 px-4 pt-5 flex items-start justify-between">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <img
            src="/woma-logo.png"
            alt="WÔMA de ARTEKNOS"
            className="w-11 h-11 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-lg shadow-[#D4AF37]/30"
            data-testid="home-logo"
          />
          <div>
            <h1
              className="text-2xl font-black italic tracking-wide drop-shadow-lg"
              style={{
                fontFamily: "Outfit, sans-serif",
                color: "#D4AF37",
                letterSpacing: "0.02em",
                textShadow: "0 2px 8px rgba(0, 71, 171, 0.6)",
              }}
              data-testid="home-brand"
            >
              WÔMA
            </h1>
            <button
              className="flex items-center gap-1 mt-1 group min-w-0 max-w-full"
              data-testid="location-selector"
            >
              <span className="text-white text-base font-semibold drop-shadow whitespace-nowrap">
                Votre position
              </span>
              {hood && (
                <span className="text-[#D4AF37] text-base font-bold drop-shadow truncate max-w-[6.5rem] sm:max-w-xs" data-testid="home-neighbourhood">
                  · {hood}
                </span>
              )}
              <ChevronRight className="w-4 h-4 text-white transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RoleSwitcher user={user} onUserUpdate={onUserUpdate} className="hidden md:block" />
          <ThemeToggle />
          <button
            onClick={() => setShowMenu(true)}
            className="w-11 h-11 flex items-center justify-center rounded-full bg-black/40 backdrop-blur hover:bg-black/60 transition-colors border border-white/10"
            data-testid="home-menu-btn"
          >
            <Menu className="w-6 h-6 text-white" />
          </button>
        </div>
      </header>

      <button
        onClick={() => setShowExplore(true)}
        className="absolute top-24 left-4 z-30 flex items-center gap-2 bg-black/60 backdrop-blur border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-black shadow-lg hover:bg-black/80 transition-colors"
        data-testid="explore-dakar-btn"
      >
        <Compass className="w-4 h-4" /> Explorer Dakar
      </button>
      {showExplore && (
        <ExploreDakarPanel pos={userPos} onClose={() => setShowExplore(false)} onSaved={fetchRecentAddresses} />
      )}
      {/* BOTTOM SHEET coulissant */}
      <BottomSheet snapPoints={["28%", "55%", "92%"]} initialIndex={0}>
        <div className="px-4 pb-10 space-y-4">
          {/* Search bar */}
          <button
            onClick={openDestinationPicker}
            className="w-full flex items-center bg-[#2D2D2D] hover:bg-[#333] rounded-full px-5 py-4 transition-colors"
            data-testid="destination-search-btn"
          >
            <Search className="w-5 h-5 text-white flex-shrink-0" />
            <span
              className="flex-1 ml-3 text-white text-base font-semibold text-left"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Où allons-nous ?
            </span>
            <ArrowRight className="w-6 h-6 text-[#A0A0A0] flex-shrink-0" />
          </button>

          {/* Services grid 2x2 */}
          <div className="grid grid-cols-2 gap-3" data-testid="services-grid">
            {SMALL_SERVICES.map((s) => (
              <ServiceCard key={s.key} service={s} bouncing={bouncing === s.key} onClick={() => tap(s.key, () => navigate(s.route))} />
            ))}
          </div>

          {/* Large "Courses" card */}
          <button
            onClick={() => navigate("/orders/new")}
            className="relative w-full bg-gradient-to-br from-[#003594] to-[#D4AF37] hover:from-[#003380] hover:to-[#0099BF] rounded-2xl p-5 h-[140px] overflow-hidden transition-all active:scale-[0.99] text-left shadow-lg shadow-[#003594]/20"
            data-testid="large-card-courses"
          >
            <h2
              className="text-white text-xl font-bold"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Courses
            </h2>
            <p className="text-white/80 text-sm mt-1">à partir de 4 min</p>
            <div className="absolute right-5 bottom-4">
              <div className="w-24 h-16 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
                <Car className="w-12 h-12 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </button>

          {/* Favoris */}
          {user && <FavoritePlaces pos={userPos} />}

          {/* Recent addresses */}
          {recentAddresses.length > 0 ? (
            <div>
              <h3 className="text-white text-sm font-semibold mb-1 px-1">Récents</h3>
              {recentAddresses.map((a, i) => {
                const { title, subtitle } = splitAddress(a.address);
                return (
                  <RecentItem
                    key={`${a.address}-${i}`}
                    title={title}
                    subtitle={subtitle}
                    time={formatRelativeTime(a.date)}
                    onClick={() => handleRecentClick(a)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="py-6 text-center">
              <p className="text-[#A0A0A0] text-sm">
                Aucune adresse récente
              </p>
            </div>
          )}
        </div>
      </BottomSheet>

      {/* DESTINATION PICKER plein écran */}
      {showPicker && (
        <DestinationPicker
          onClose={() => setShowPicker(false)}
          onConfirm={handlePickerConfirm}
          initialCenter={userPos}
        />
      )}

      {/* SIDE MENU */}
      {showMenu && (
        <div
          className="fixed inset-0 z-50 bg-black/70"
          onClick={() => setShowMenu(false)}
          data-testid="side-menu"
        >
          <div
            className="absolute right-0 top-0 bottom-0 w-80 max-w-[90vw] bg-[#1A1A1A] border-l border-[#333] p-6 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-[#A0A0A0] text-xs">Connecté</p>
                <p className="text-white font-bold text-base">
                  {user?.full_name || user?.email || "Client"}
                </p>
              </div>
              <button
                onClick={() => setShowMenu(false)}
                className="text-white w-9 h-9 rounded-full hover:bg-[#2D2D2D] flex items-center justify-center"
              >
                ✕
              </button>
            </div>
            <nav className="space-y-1">
              {EXTRA_SERVICES.map((s) => {
                const Icon = s.Icon;
                return (
                  <button
                    key={s.key}
                    onClick={() => {
                      setShowMenu(false);
                      navigate(s.route);
                    }}
                    className="w-full flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-[#2D2D2D] transition-colors text-left"
                    data-testid={`menu-${s.key}`}
                  >
                    <Icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                    <span className="text-white text-base">{s.title}</span>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setShowMenu(false);
                  navigate("/dashboard");
                }}
                className="w-full flex items-center gap-3 py-3 px-3 rounded-xl hover:bg-[#2D2D2D] transition-colors text-left"
                data-testid="menu-dashboard"
              >
                <Clock className="w-5 h-5 text-white" strokeWidth={1.8} />
                <span className="text-white text-base">Historique complet</span>
              </button>
            </nav>
            <div className="mt-6 pt-6 border-t border-[#2D2D2D]">
              <AccountMenuLinks user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} onNavigate={() => setShowMenu(false)} />
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Premium — Welcome + 4 Services + 500 FCFA */}
      {showWelcome && (
        <WelcomeBonusOnboarding
          user={user}
          onClose={dismissWelcome}
          onCreditUpdate={() => {
            // Solde rafraîchi côté hook; l'utilisateur verra la mise à jour via login subséquent ou navigation
          }}
        />
      )}
    </div>
  );
}
